/**
 * A synthetic row of the review: an unrecognized instrument the user classified, or one whose new alias
 * the engine does not recognise either. It has no price and no share count, so it is held by value and
 * its market value is the one cell of the review the user types. It starts on the file's market value,
 * when the file gives one.
 */
import type { ColDef } from "ag-grid-community";
import { deriveWeight } from "$root/functional-areas/instruments-editor/quantity/derive";
import { parseAmount } from "$root/functional-areas/instruments-editor/quantity/columns";
import { type QuantityReviewRow, rebalanceReviewCash } from "./rows";

/** `row` as a synthetic instrument, holding `row`'s market value. */
export function toSynthetic(row: QuantityReviewRow, aum: number): QuantityReviewRow {
	return {
		...row,
		synthetic: true,
		heldByValue: true,
		quantity: undefined,
		price: undefined,
		unpriced: false,
		weight: row.marketValue == null ? undefined : deriveWeight(row.marketValue, aum),
		positionSizing: undefined,
		aliasUnsized: undefined,
	};
}

/** Writes `marketValue` on the synthetic row `id`, then re-derives cash. Any other row is left as it is. */
export function setSyntheticMarketValue(
	rows: QuantityReviewRow[],
	id: string,
	marketValue: number | undefined,
	aum: number,
): QuantityReviewRow[] {
	return rebalanceReviewCash(
		rows.map((row) =>
			row.id === id && row.synthetic
				? { ...row, marketValue, weight: marketValue == null ? undefined : deriveWeight(marketValue, aum) }
				: row,
		),
		aum,
	);
}

/** Whether the row is synthetic and still has no market value: it cannot be saved like that. */
export function isUnvaluedSynthetic(row: QuantityReviewRow): boolean {
	return Boolean(row.synthetic) && row.marketValue == null;
}

/**
 * `columns` with the Market value cell editable on a kept synthetic row, committing through
 * `onMarketValue`. The commit happens in the `valueSetter`: the column's value is derived, so AG Grid
 * re-reads the old one before `onCellValueChanged`. An emptied cell clears the value; a negative or
 * non-numeric one is refused.
 */
export function withSyntheticMarketValue(
	columns: ColDef<QuantityReviewRow>[],
	onMarketValue: (id: string, marketValue: number | undefined) => void,
): ColDef<QuantityReviewRow>[] {
	return columns.map((column) => {
		if (column.colId !== "MARKET_VALUE") {
			return column;
		}
		return {
			...column,
			editable: (params) => Boolean(params.data?.synthetic && !params.data.deleted),
			valueParser: parseAmount,
			valueSetter(params) {
				const value: unknown = params.newValue;
				if (value == null) {
					onMarketValue(params.data.id, undefined);
					return true;
				}
				if (typeof value !== "number" || Number.isNaN(value) || value < 0) {
					return false;
				}
				onMarketValue(params.data.id, value);
				return true;
			},
		};
	});
}
